import { toast } from "../components/razor-reuse";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../utils/axios";
import { generateQueryString } from "../helper/helpers";
import {
  AudiencePreviewPayload,
  GetNotificationCampaignsParams,
  NotificationCampaignPayload,
  NotificationCampaignState,
} from "./type";

export const getNotificationCampaigns = createAsyncThunk(
  "web/get-notification-campaigns",
  async (payload: GetNotificationCampaignsParams, thunkAPI) => {
    try {
      const data = await axios.get(
        `/admin/notification-campaigns${generateQueryString(payload)}`
      );

      if (data?.data?.status === "fail") {
        if (typeof data.data === "string") {
          toast.error(data.data);
        } else
          toast.error(data?.data?.message, {
            position: "top-right",
          });
        return thunkAPI.rejectWithValue(data);
      }
      if (data?.data?.status) {
        thunkAPI.dispatch(SET_CAMPAIGNS(data?.data));
        return data;
      }
      return data;
    } catch (error: any) {
      if (error.message === "Network Error") {
        toast.error(error.message, {
          position: "top-right",
        });
      }
      if (
        error.response?.data?.status === "fail" &&
        error.response?.status !== 401
      ) {
        return thunkAPI.rejectWithValue(error);
      }
      return error;
    }
  }
);

export const getNotificationCampaignStats = createAsyncThunk(
  "web/get-notification-campaign-stats",
  async (_payload: any, thunkAPI) => {
    try {
      const data = await axios.get(`/admin/notification-campaigns/stats`);

      if (data?.data?.status === "fail") {
        if (typeof data.data === "string") {
          toast.error(data.data);
        } else
          toast.error(data?.data?.message, {
            position: "top-right",
          });
        return thunkAPI.rejectWithValue(data);
      }
      if (data?.data?.status) {
        thunkAPI.dispatch(SET_STATS(data?.data?.data));
        return data;
      }
      return data;
    } catch (error: any) {
      if (error.message === "Network Error") {
        toast.error(error.message, {
          position: "top-right",
        });
      }
      if (
        error.response?.data?.status === "fail" &&
        error.response?.status !== 401
      ) {
        return thunkAPI.rejectWithValue(error);
      }
      return error;
    }
  }
);

export const getNotificationCampaign = createAsyncThunk(
  "web/get-notification-campaign",
  async (id: string | number, thunkAPI) => {
    try {
      const data = await axios.get(`/admin/notification-campaigns/${id}`);

      if (data?.data?.status === "fail") {
        if (typeof data.data === "string") {
          toast.error(data.data);
        } else
          toast.error(data?.data?.message, {
            position: "top-right",
          });
        return thunkAPI.rejectWithValue(data);
      }
      return data;
    } catch (error: any) {
      if (error.message === "Network Error") {
        toast.error(error.message, {
          position: "top-right",
        });
      }
      if (
        error.response?.data?.status === "fail" &&
        error.response?.status !== 401
      ) {
        return thunkAPI.rejectWithValue(error);
      }
      return error;
    }
  }
);

export const createNotificationCampaign = createAsyncThunk(
  "web/create-notification-campaign",
  async (payload: NotificationCampaignPayload, thunkAPI) => {
    try {
      const data = await axios.post(`/admin/notification-campaigns`, payload);

      // The shared axios instance's response interceptor resolves instead
      // of rejecting on most errors, so a failed request can land here as
      // an AxiosError rather than throwing — route it into the same
      // handling as a thrown error instead of treating it as a success.
      if ((data as any)?.isAxiosError) {
        throw data;
      }

      if (data?.data?.status === "fail") {
        if (typeof data.data === "string") {
          toast.error(data.data);
        } else
          toast.error(data?.data?.message, {
            position: "top-right",
          });
        return thunkAPI.rejectWithValue(data);
      }
      if (data?.data?.status) {
        toast.success(data?.data?.message, {
          position: "top-right",
          theme: "colored",
        });
        return data;
      }
      return data;
    } catch (error: any) {
      if (!error?.response) {
        // No HTTP response at all — the request never reached the server
        // (offline, DNS failure, server unreachable, timed out, etc.).
        toast.error(
          "You appear to be offline. Please check your connection and try again.",
          { position: "top-right" },
        );
      } else if (error.response?.data?.status === "fail") {
        if (typeof error.response.data === "string") {
          toast.error(error.response.data);
        } else {
          toast.error(error.response.data?.message, { position: "top-right" });
        }
      }
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const updateNotificationCampaign = createAsyncThunk(
  "web/update-notification-campaign",
  async (
    payload: { id: string | number; data: NotificationCampaignPayload },
    thunkAPI
  ) => {
    try {
      const data = await axios.put(
        `/admin/notification-campaigns/${payload.id}`,
        payload.data
      );

      if ((data as any)?.isAxiosError) {
        throw data;
      }

      if (data?.data?.status === "fail") {
        if (typeof data.data === "string") {
          toast.error(data.data);
        } else
          toast.error(data?.data?.message, {
            position: "top-right",
          });
        return thunkAPI.rejectWithValue(data);
      }
      if (data?.data?.status) {
        toast.success(data?.data?.message, {
          position: "top-right",
          theme: "colored",
        });
        return data;
      }
      return data;
    } catch (error: any) {
      if (!error?.response) {
        toast.error(
          "You appear to be offline. Please check your connection and try again.",
          { position: "top-right" },
        );
      } else if (error.response?.data?.status === "fail") {
        if (typeof error.response.data === "string") {
          toast.error(error.response.data);
        } else {
          toast.error(error.response.data?.message, { position: "top-right" });
        }
      }
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const deleteNotificationCampaign = createAsyncThunk(
  "web/delete-notification-campaign",
  async (id: string | number, thunkAPI) => {
    try {
      const data = await axios.delete(`/admin/notification-campaigns/${id}`);

      if (data?.data?.status === "fail") {
        if (typeof data.data === "string") {
          toast.error(data.data);
        } else
          toast.error(data?.data?.message, {
            position: "top-right",
          });
        return thunkAPI.rejectWithValue(data);
      }
      if (data?.data?.status) {
        toast.success(data?.data?.message, {
          position: "top-right",
          theme: "colored",
        });
        return data;
      }
      return data;
    } catch (error: any) {
      if (error.message === "Network Error") {
        toast.error(error.message, {
          position: "top-right",
        });
      }
      if (
        error.response?.data?.status === "fail" &&
        error.response?.status !== 401
      ) {
        return thunkAPI.rejectWithValue(error);
      }
      return error;
    }
  }
);

export const previewNotificationAudience = createAsyncThunk(
  "web/preview-notification-audience",
  async (payload: AudiencePreviewPayload, thunkAPI) => {
    try {
      const data = await axios.post(
        `/admin/notification-campaigns/audience/preview`,
        payload
      );

      if (data?.data?.status === "fail") {
        if (typeof data.data === "string") {
          toast.error(data.data);
        } else
          toast.error(data?.data?.message, {
            position: "top-right",
          });
        return thunkAPI.rejectWithValue(data);
      }
      if (data?.data?.status) {
        // Response shape isn't documented (shown as `null`) — fall back
        // across the field names a count is most likely to be under.
        const count =
          data?.data?.data?.matched_count ??
          data?.data?.data?.count ??
          data?.data?.data ??
          0;
        thunkAPI.dispatch(SET_AUDIENCE_PREVIEW_COUNT(Number(count) || 0));
        return data;
      }
      return data;
    } catch (error: any) {
      if (error.message === "Network Error") {
        toast.error(error.message, {
          position: "top-right",
        });
      }
      if (
        error.response?.data?.status === "fail" &&
        error.response?.status !== 401
      ) {
        return thunkAPI.rejectWithValue(error);
      }
      return error;
    }
  }
);

export const uploadNotificationAudienceCsv = createAsyncThunk(
  "web/upload-notification-audience-csv",
  async (file: File, thunkAPI) => {
    try {
      const formData = new FormData();
      formData.append("audience_file", file);

      const data = await axios.post(
        `/admin/notification-campaigns/audience/csv`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (data?.data?.status === "fail") {
        if (typeof data.data === "string") {
          toast.error(data.data);
        } else
          toast.error(data?.data?.message, {
            position: "top-right",
          });
        return thunkAPI.rejectWithValue(data);
      }
      if (data?.data?.status) {
        // Response shape isn't documented (shown as `null`) — the endpoint
        // description says it returns matched user ids + unmatched emails.
        const result = {
          matched_user_ids: data?.data?.data?.matched_user_ids || [],
          unmatched_emails: data?.data?.data?.unmatched_emails || [],
        };
        thunkAPI.dispatch(SET_AUDIENCE_CSV_RESULT(result));
        return data;
      }
      return data;
    } catch (error: any) {
      if (error.message === "Network Error") {
        toast.error(error.message, {
          position: "top-right",
        });
      }
      if (
        error.response?.data?.status === "fail" &&
        error.response?.status !== 401
      ) {
        return thunkAPI.rejectWithValue(error);
      }
      return error;
    }
  }
);

// Served from the same host as the API (REACT_APP_BASE_URL) but outside the
// /v1 versioned path, so this tracks dev/staging/prod automatically instead
// of pointing at one environment's domain.
const API_ORIGIN = new URL(process.env.REACT_APP_BASE_URL || "").origin;
const AUDIENCE_TEMPLATE_URL = `${API_ORIGIN}/templates/notification_audience_template.csv`;

export const downloadNotificationAudienceTemplate = createAsyncThunk(
  "web/download-notification-audience-template",
  async (_payload: any, thunkAPI) => {
    try {
      // Deliberately NOT fetched via axios/blob: reading a cross-origin
      // response's bytes in JS requires the server to send CORS headers on
      // that exact route, which a plain static-file route often doesn't get
      // configured the same way the JSON API routes are (the app runs on a
      // different origin than the API host, so this is cross-origin even
      // though both URLs point at "the same backend"). A direct navigation
      // isn't a cross-origin read, so it isn't subject to that check — the
      // tradeoff is it can't carry the Authorization header, so this only
      // works if the template is reachable without auth.
      const opened = window.open(AUDIENCE_TEMPLATE_URL, "_blank");
      if (!opened) {
        toast.error("Pop-up blocked — allow pop-ups to download the template", {
          position: "top-right",
        });
        return thunkAPI.rejectWithValue("popup_blocked");
      }
    } catch (error: any) {
      toast.error("Failed to download template", {
        position: "top-right",
      });
      return thunkAPI.rejectWithValue(error);
    }
  }
);

const initialState: NotificationCampaignState = {
  loading_campaigns: false,
  campaigns: [],
  campaigns_meta: undefined,
  loading_stats: false,
  stats: undefined,
  loading_create: false,
  loading_update: false,
  loading_delete:false,
  loading_audience_preview: false,
  audience_preview_count: undefined,
  loading_audience_csv: false,
  audience_csv_result: undefined,
  downloading_audience_template: false,
};

export const notificationCampaignSlice = createSlice({
  name: "notification_campaign",
  initialState,
  reducers: {
    SET_CAMPAIGNS: (state, action) => {
      state.campaigns = action.payload?.data || [];
      state.campaigns_meta = {
        current_page: action.payload?.meta?.current_page,
        per_page: action.payload?.meta?.per_page,
        total: action.payload?.meta?.total,
        last_page: action.payload?.meta?.last_page,
      };
    },
    SET_STATS: (state, action) => {
      state.stats = action.payload;
    },
    SET_AUDIENCE_PREVIEW_COUNT: (state, action) => {
      state.audience_preview_count = action.payload;
    },
    SET_AUDIENCE_CSV_RESULT: (state, action) => {
      state.audience_csv_result = action.payload;
    },
    RESET_AUDIENCE_PREVIEW: (state) => {
      state.audience_preview_count = undefined;
    },
    RESET_AUDIENCE_CSV_RESULT: (state) => {
      state.audience_csv_result = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getNotificationCampaigns.pending, (state) => {
      state.loading_campaigns = true;
    });
    builder.addCase(getNotificationCampaigns.fulfilled, (state) => {
      state.loading_campaigns = false;
    });
    builder.addCase(getNotificationCampaigns.rejected, (state) => {
      state.loading_campaigns = false;
    });
    builder.addCase(getNotificationCampaignStats.pending, (state) => {
      state.loading_stats = true;
    });
    builder.addCase(getNotificationCampaignStats.fulfilled, (state) => {
      state.loading_stats = false;
    });
    builder.addCase(getNotificationCampaignStats.rejected, (state) => {
      state.loading_stats = false;
    });
    builder.addCase(createNotificationCampaign.pending, (state) => {
      state.loading_create = true;
    });
    builder.addCase(createNotificationCampaign.fulfilled, (state) => {
      state.loading_create = false;
    });
    builder.addCase(createNotificationCampaign.rejected, (state) => {
      state.loading_create = false;
    });
    builder.addCase(updateNotificationCampaign.pending, (state) => {
      state.loading_update = true;
    });
    builder.addCase(updateNotificationCampaign.fulfilled, (state) => {
      state.loading_update = false;
    });
    builder.addCase(updateNotificationCampaign.rejected, (state) => {
      state.loading_update = false;
    });
    builder.addCase(previewNotificationAudience.pending, (state) => {
      state.loading_audience_preview = true;
    });
    builder.addCase(previewNotificationAudience.fulfilled, (state, { payload: { data } }) => {
      state.loading_audience_preview = false;
      state.audience_preview_count = data?.data?.estimated_recipient_count || 0
    });
    builder.addCase(previewNotificationAudience.rejected, (state) => {
      state.loading_audience_preview = false;
    });
    builder.addCase(uploadNotificationAudienceCsv.pending, (state) => {
      state.loading_audience_csv = true;
    });
    builder.addCase(uploadNotificationAudienceCsv.fulfilled, (state) => {
      state.loading_audience_csv = false;
    });
     builder.addCase(deleteNotificationCampaign.pending, (state) => {
      state.loading_delete = true;
    });
    builder.addCase(deleteNotificationCampaign.fulfilled, (state) => {
      state.loading_delete = false;
    });
    builder.addCase(deleteNotificationCampaign.rejected, (state) => {
      state.loading_delete = false;
    });
    builder.addCase(uploadNotificationAudienceCsv.rejected, (state) => {
      state.loading_audience_csv = false;
    });
    builder.addCase(downloadNotificationAudienceTemplate.pending, (state) => {
      state.downloading_audience_template = true;
    });
    builder.addCase(downloadNotificationAudienceTemplate.fulfilled, (state) => {
      state.downloading_audience_template = false;
    });
    builder.addCase(downloadNotificationAudienceTemplate.rejected, (state) => {
      state.downloading_audience_template = false;
    });
  },
});

export const {
  SET_CAMPAIGNS,
  SET_STATS,
  SET_AUDIENCE_PREVIEW_COUNT,
  SET_AUDIENCE_CSV_RESULT,
  RESET_AUDIENCE_PREVIEW,
  RESET_AUDIENCE_CSV_RESULT,
} = notificationCampaignSlice.actions;

export default notificationCampaignSlice.reducer;
